import { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Filter, Loader2, X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { parseCampaignsPayload } from '@/utils/entityCrossFilter';
import {
  CAMPAIGN_SEARCH_PLACEHOLDER,
  filterCampaignsBySearch,
} from '@/utils/campaignSearch';
import { normalizeCampaignId } from '@/utils/budgetRuleCampaigns';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { campaignAdProductLabel, type SpCampaignNormalized } from '@/types/spCampaigns';

function campaignFilterLabel(
  selectedIds: string[],
  options: SpCampaignNormalized[]
): string {
  if (selectedIds.length === 0) return 'All campaigns';
  if (selectedIds.length === 1) {
    const match = options.find(
      (c) => normalizeCampaignId(c.campaignId) === normalizeCampaignId(selectedIds[0] ?? '')
    );
    return match?.name || selectedIds[0] || '1 campaign';
  }
  return `${selectedIds.length} campaigns selected`;
}

function toggleCampaignId(selectedIds: string[], campaignId: string, checked: boolean): string[] {
  const normalized = normalizeCampaignId(campaignId);
  if (checked) {
    if (selectedIds.some((id) => normalizeCampaignId(id) === normalized)) return selectedIds;
    return [...selectedIds, campaignId];
  }
  return selectedIds.filter((id) => normalizeCampaignId(id) !== normalized);
}

function resolveCampaignName(
  campaignId: string,
  options: SpCampaignNormalized[]
): string {
  const match = options.find(
    (c) => normalizeCampaignId(c.campaignId) === normalizeCampaignId(campaignId)
  );
  return match?.name || campaignId;
}

export function DaypartingCampaignFilter({
  profileId,
  selectedCampaignIds,
  onChange,
  replacesFilterLabel = null,
}: {
  profileId: string;
  selectedCampaignIds: string[];
  onChange: (campaignIds: string[]) => void;
  replacesFilterLabel?: string | null;
}) {
  const labelId = useId();
  const searchInputId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draftCampaignIds, setDraftCampaignIds] = useState<string[]>([]);
  const [confirmReplace, setConfirmReplace] = useState(false);

  const campaignsQuery = useQuery({
    queryKey: ['dayparting-campaign-options', profileId],
    queryFn: async () => {
      const res = await adsApi.getProfileCampaigns(profileId, null, {
        status: 'ACTIVE',
      });
      if (!res.success) throw new Error('Failed to load campaigns');
      return parseCampaignsPayload(res.data).filter(
        (campaign) => campaign.state.toUpperCase() === 'ENABLED'
      );
    },
    enabled: Boolean(profileId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const options = useMemo(() => {
    const campaigns = campaignsQuery.data ?? [];
    return [...campaigns].sort((a, b) =>
      (a.name || a.campaignId).localeCompare(b.name || b.campaignId, undefined, {
        sensitivity: 'base',
      })
    );
  }, [campaignsQuery.data]);

  const visibleOptions = useMemo(
    () => filterCampaignsBySearch(options, search, 100),
    [options, search]
  );

  const activeCampaignIds = open ? draftCampaignIds : selectedCampaignIds;
  const selectedSet = useMemo(
    () => new Set(activeCampaignIds.map((id) => normalizeCampaignId(id))),
    [activeCampaignIds]
  );

  const triggerLabel = useMemo(
    () => campaignFilterLabel(selectedCampaignIds, options),
    [selectedCampaignIds, options]
  );

  const loading = campaignsQuery.isPending || campaignsQuery.isFetching;
  const isFiltered = selectedCampaignIds.length > 0;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    setConfirmReplace(false);
    if (nextOpen) {
      setDraftCampaignIds(selectedCampaignIds);
      return;
    }
    setDraftCampaignIds(selectedCampaignIds);
    setSearch('');
  };

  const updateDraftCampaignIds = (next: string[]) => {
    setDraftCampaignIds(next);
    if (next.length === 0) setConfirmReplace(false);
  };

  const selectVisible = () => {
    const next = new Set(draftCampaignIds.map((id) => normalizeCampaignId(id)));
    const merged = [...draftCampaignIds];
    for (const campaign of visibleOptions) {
      const normalized = normalizeCampaignId(campaign.campaignId);
      if (next.has(normalized)) continue;
      next.add(normalized);
      merged.push(campaign.campaignId);
    }
    updateDraftCampaignIds(merged);
  };

  const applyDraft = () => {
    onChange(draftCampaignIds);
    setOpen(false);
    setSearch('');
    setConfirmReplace(false);
  };

  const handleDone = () => {
    if (replacesFilterLabel && draftCampaignIds.length > 0) {
      setConfirmReplace(true);
      return;
    }
    applyDraft();
  };

  return (
    <div
      className="rounded-xl border border-border bg-surface p-3 shadow-sm sm:p-4"
      role="group"
      aria-labelledby={labelId}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <p id={labelId} className="text-sm font-semibold text-foreground">
            Campaign filter
          </p>
          <p className="text-xs text-foreground-subtle">
            Limit the heatmap and summary cards to specific campaigns.
            Leave unselected to include all campaigns. The list shows enabled campaigns only.
            {replacesFilterLabel
              ? ' Applying a campaign filter will replace the current portfolio filter.'
              : ''}
          </p>
        </div>

        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={!profileId}
              aria-labelledby={labelId}
              aria-haspopup="listbox"
              aria-expanded={open}
              className={cn(
                'inline-flex w-full min-w-[14rem] max-w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm lg:w-auto lg:min-w-[18rem]',
                'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                isFiltered
                  ? 'border-brand/40 bg-brand-subtle/40 text-foreground hover:bg-brand-subtle/60'
                  : 'border-border bg-page text-foreground hover:bg-page/80'
              )}
            >
              <span className="inline-flex min-w-0 items-center gap-2">
                <Filter
                  className={cn(
                    'h-4 w-4 shrink-0',
                    isFiltered ? 'text-brand-deep' : 'text-foreground-muted'
                  )}
                  aria-hidden
                />
                <span className="truncate text-left">{triggerLabel}</span>
              </span>
              {loading ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-foreground-muted" aria-hidden />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[min(26rem,calc(100vw-2rem))] p-0">
            <div className="space-y-3 border-b border-border px-3 py-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-foreground">Select campaigns</p>
                  <p className="text-xs text-foreground-subtle">
                    Search by name or ID. Check one or more campaigns.
                  </p>
                </div>
                {isFiltered ? (
                  <button
                    type="button"
                    className="shrink-0 text-xs font-medium text-foreground-muted hover:text-foreground"
                    onClick={() => updateDraftCampaignIds([])}
                  >
                    Clear all
                  </button>
                ) : null}
              </div>
              <div>
                <label htmlFor={searchInputId} className="sr-only">
                  Search campaigns
                </label>
                <input
                  id={searchInputId}
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={CAMPAIGN_SEARCH_PLACEHOLDER}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                  autoFocus
                />
              </div>
              {visibleOptions.length > 0 ? (
                <button
                  type="button"
                  className="text-xs font-medium text-brand-deep hover:underline"
                  onClick={selectVisible}
                >
                  Select all matching ({visibleOptions.length})
                </button>
              ) : null}
            </div>

            <div className="max-h-64 overflow-y-auto px-1 py-1" role="listbox" aria-multiselectable="true">
              {campaignsQuery.isError ? (
                <p className="px-3 py-4 text-sm text-error">Failed to load campaigns.</p>
              ) : loading && options.length === 0 ? (
                <p className="px-3 py-4 text-sm text-foreground-muted">Loading campaigns…</p>
              ) : visibleOptions.length === 0 ? (
                <p className="px-3 py-4 text-sm text-foreground-muted">No enabled campaigns match.</p>
              ) : (
                <ul className="m-0 list-none p-0">
                  {visibleOptions.map((campaign) => {
                    const checked = selectedSet.has(normalizeCampaignId(campaign.campaignId));
                    return (
                      <li key={campaign.campaignId} role="option" aria-selected={checked}>
                        <label
                          className={cn(
                            'flex cursor-pointer items-start gap-3 rounded-lg px-2.5 py-2.5 text-sm',
                            checked ? 'bg-brand-subtle/50' : 'hover:bg-surface-muted'
                          )}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-brand/30"
                            checked={checked}
                            onChange={(e) =>
                              updateDraftCampaignIds(
                                toggleCampaignId(
                                  draftCampaignIds,
                                  campaign.campaignId,
                                  e.target.checked
                                )
                              )
                            }
                          />
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-foreground">
                              {campaign.name || campaign.campaignId}
                            </span>
                            <span className="block truncate text-xs text-foreground-muted">
                              {campaignAdProductLabel(campaign.adProduct)}
                              {' · '}
                              {campaign.campaignId}
                              {campaign.state ? ` · ${campaign.state}` : ''}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="space-y-2 border-t border-border px-3 py-2.5">
              {confirmReplace ? (
                <>
                  <p className="text-xs text-foreground-subtle">
                    Apply these campaigns? This will replace the current portfolio filter ({replacesFilterLabel}).
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setConfirmReplace(false)}>
                      Back
                    </Button>
                    <Button size="sm" onClick={applyDraft}>
                      Replace and apply
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-foreground-subtle">
                    {draftCampaignIds.length > 0
                      ? `${draftCampaignIds.length} campaign${draftCampaignIds.length === 1 ? '' : 's'} selected`
                      : 'All campaigns included'}
                    {replacesFilterLabel && draftCampaignIds.length > 0
                      ? ' · This will replace the current portfolio filter.'
                      : ''}
                  </p>
                  <Button variant="secondary" size="sm" onClick={handleDone}>
                    Done
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {isFiltered ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border/70 pt-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">
            Active
          </span>
          {selectedCampaignIds.map((campaignId) => (
            <span
              key={campaignId}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-brand/30 bg-brand-subtle/60 px-2.5 py-1 text-xs text-foreground"
            >
              <span className="truncate">{resolveCampaignName(campaignId, options)}</span>
              <button
                type="button"
                className="shrink-0 rounded-full text-foreground-muted hover:text-foreground"
                aria-label={`Remove ${resolveCampaignName(campaignId, options)}`}
                onClick={() =>
                  onChange(toggleCampaignId(selectedCampaignIds, campaignId, false))
                }
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
          <button
            type="button"
            className="text-xs font-medium text-foreground-muted hover:text-foreground"
            onClick={() => onChange([])}
          >
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  );
}
